import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {observer} from 'mobx-react'
import {
    Avatar,
    Card as AntCard,
    Descriptions,
    Typography,
    Row,
    Col,
    Tooltip
} from 'antd'
import {RightOutlined} from '@ant-design/icons'
import classnames from 'classnames'

import {useScreenType} from '@/hooks'
import {Card, Status} from '@/components'

import {AccountNumberView, AccountTypeView} from '@/pages/BankAccounts/components'
import {GenericBank, TrashSvg} from '~/assets/icons'
import {formatSortCode} from '@/services/utils'
import {hasPermissions} from '@/stores/auth/services'
import {PermissionsMap} from '@/stores/auth/constants/permissions-map'
import {TagType} from '@/components/dumb/Tag/props'
import {DossierBankAccountType} from '@/stores/bank-accounts'
import {GoogleManagerTagClassNames} from '@/constants'
import {BANK_ACCOUNT_CARD} from '@/constants/playwright-ids'

import {BankAccountCardProps} from './props'
import {BankAccountDeleteModal} from './BankAccountDeleteModal'
import styles from './BankAccountCard.scss'

export const BankAccountCard: React.FC<BankAccountCardProps> = observer(
    ({
        bank,
        accountName,
        accountNumber,
        associatedSettlementsOutlets,
        accountTypes,
        sortCode,
        logo,
        onClick,
        onChangeBankDetailsClick,
        onDelete,
        onDeleteSuccess,
        bankDetailId,
        isSettlements,
        isDirectDebit,
        loading,
        className,
        hasSettlementBlocks,
        hasAllBlocks,
        hasDirectDebitBlocks,
        hasFullPermissions,
        hasChangeBankDetails = false
    }) => {
        const {t} = useTranslation()
        const {isMobile} = useScreenType()
        const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
        const [isDeleting, setIsDeleting] = useState(false)


        const type: DossierBankAccountType = isSettlements ? 'settlements' : 'direct-debit'

        const handleDeleteClick = (e: React.MouseEvent) => {
            e.stopPropagation()
            setIsDeleteModalOpen(true)
        }

        const handleDeleteConfirm = async () => {
            setIsDeleting(true)
            try {
                await onDelete?.(bankDetailId)
                setIsDeleteModalOpen(false)
                onDeleteSuccess?.()
            } catch (error) {
                console.error('Failed to delete bank account:', error)
            } finally {
                setIsDeleting(false)
            }
        }

        const handleDeleteCancel = () => {
            setIsDeleteModalOpen(false)
        }

        const renderChangeBankDetailsButton = (text: string, tagClassName: string) => {
            return (
                <Status
                    className={styles.changeBankDetails}
                    tagClassName={tagClassName}
                    key={'changeBankDetails'}
                    status={text}
                    type={TagType.Processing}
                    iconType={'settings'}
                    clickable
                    onClick={() => onChangeBankDetailsClick(type)}
                />
            )
        }

        const renderWaitingStatus = () => {
            return (
                <Status
                    className={styles.changeBankDetails}
                    key={'changeInProgress'}
                    status={t('BankAccountCard.labels.changeInProgress')}
                    type={TagType.Default}
                    iconType={'clock'}
                />
            )
        }

        return (
            <>
                <BankAccountDeleteModal
                    open={isDeleteModalOpen}
                    isDeleting={isDeleting}
                    onConfirm={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                />
                <Card
                    loading={loading}
                    hoverable={!!onClick}
                    className={classnames(styles.card, className)}
                    onClick={onClick && onClick}
                    data-test-id={BANK_ACCOUNT_CARD}
                >
                    
                    <AntCard.Meta
                        avatar={<Avatar className={styles.avatar} src={logo || <GenericBank />} />}
                        title={
                            <Row justify={'space-between'} align={'middle'}>
                                <Col>{accountName}</Col>
                                {hasPermissions([PermissionsMap.merchant.bank_accounts.full]) && (
                                    <Col>
                                        <div className={styles.iconContainer}>
                                            {onDelete &&
                                            bankDetailId &&
                                            (!accountTypes || !accountTypes.length) && (
                                                <>
                                                    <Tooltip
                                                        title={t(
                                                            'BankAccountCard.tooltips.removeBankAccount'
                                                        )}
                                                    >
                                                        <TrashSvg
                                                            className={styles.trashIcon}
                                                            onClick={handleDeleteClick}
                                                        />
                                                    </Tooltip>
                                                </>
                                            )}
                                            {isMobile && (<RightOutlined className={styles.rightIcon} />)}
                                        </div>    
                                    </Col>
                                )}
                            </Row>
                        }
                        description={
                            <>
                                <Descriptions
                                    className={styles.descriptionLabel}
                                    title=''
                                    layout={'vertical'}
                                    column={1}
                                >
                                    {bank && (
                                        <Descriptions.Item label={t('BankAccountCard.labels.bank')}>
                                            {bank}
                                        </Descriptions.Item>
                                    )}
                                    {sortCode && (
                                        <Descriptions.Item
                                            label={t('BankAccountCard.labels.sortCode')}
                                        >
                                            {formatSortCode(sortCode)}
                                        </Descriptions.Item>
                                    )}
                                    <Descriptions.Item
                                        label={t('BankAccountCard.labels.accountNumber')}
                                    >
                                        <AccountNumberView accountNumber={accountNumber} />
                                    </Descriptions.Item>
                                    {associatedSettlementsOutlets && (
                                        <Descriptions.Item
                                            label={t('BankAccountCard.labels.associatedOutlets')}
                                        >
                                            {associatedSettlementsOutlets.length > 0 ? (
                                                <Typography.Text className={styles.truncate}>
                                                    {associatedSettlementsOutlets
                                                        .map((outlet) => outlet.trim())
                                                        .join(', ')}
                                                </Typography.Text>
                                            ) : (
                                                <Typography.Text type={'secondary'}>
                                                    {t(
                                                        'BankAccountCard.labels.noAssociatedOutlets'
                                                    )}
                                                </Typography.Text>
                                            )}
                                        </Descriptions.Item>
                                    )}
                                </Descriptions>
                                {accountTypes && <AccountTypeView accountTypes={accountTypes} />}
                                {hasFullPermissions &&
                                    hasChangeBankDetails &&
                                    isSettlements &&
                                    !(hasSettlementBlocks || hasAllBlocks) &&
                                    renderChangeBankDetailsButton(
                                        t('BankAccountCard.labels.changeSettlementsBankDetails'),
                                        GoogleManagerTagClassNames.settings.outlets
                                            .changeBankDetails.changeSettlementsButton
                                    )}
                                {hasFullPermissions &&
                                    hasChangeBankDetails &&
                                    isDirectDebit &&
                                    !(hasDirectDebitBlocks || hasAllBlocks) &&
                                    renderChangeBankDetailsButton(
                                        t('BankAccountCard.labels.changeDirectDebitBankDetails'),
                                        GoogleManagerTagClassNames.settings.outlets
                                            .changeBankDetails.changeDirectDebitButton
                                    )}
                                {isSettlements &&
                                    (hasSettlementBlocks || hasAllBlocks) &&
                                    renderWaitingStatus()}
                                {isDirectDebit &&
                                    (hasDirectDebitBlocks || hasAllBlocks) &&
                                    renderWaitingStatus()}
                            </>
                        }
                    />
                </Card>
            </>
        )
    }
)
